import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, X, User } from "lucide-react";
import { normalizeSexToA, normalizeSexToB, isSexConstraintError } from "@/lib/normalize";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";


type Patient = {
  id?: string;
  full_name: string;
  cpf: string;
  email?: string;
  celular?: string;
  data_nascimento?: string;
  sexo?: string;
  avatar_url?: string;
};

export function PatientFormSheet({
  open, onClose, onSaved, patient
}: {
  open: boolean; onClose: () => void; onSaved: () => void; patient?: Patient | null;
}) {
  const { refreshPatientsCount, canAddPatient, patientsCount } = useSubscriptionStore();
  const [form, setForm] = useState<Patient>({
    full_name: "",
    cpf: "",
    email: "",
    celular: "",
    data_nascimento: "",
    sexo: "",
    avatar_url: ""
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    // Verificar limite ao abrir o sheet para novo paciente
    if (open && !patient && !canAddPatient()) {
      // Não abrir o sheet, apenas mostrar modal de upgrade
      setShowUpgradeModal(true);
      onClose();
      return;
    }

    if (open && patient) {
      setForm({
        full_name: patient.full_name || "",
        cpf: patient.cpf || "",
        email: patient.email || "",
        celular: patient.celular || "",
        data_nascimento: patient.data_nascimento || "",
        sexo: patient.sexo || "",
        avatar_url: patient.avatar_url || ""
      });
      setAvatarPreview(patient.avatar_url || null);
    } else if (open && !patient) {
      setForm({
        full_name: "",
        cpf: "",
        email: "",
        celular: "",
        data_nascimento: "",
        sexo: "",
        avatar_url: ""
      });
      setAvatarPreview(null);
    }
    setAvatarFile(null);
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagem deve ter no máximo 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Arquivo deve ser uma imagem");
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setForm(f => ({ ...f, avatar_url: "" }));
  };

  async function handleSave() {
    try {
      setBusy(true);
      setErr(null);

      if (!form.full_name.trim()) throw new Error("Nome é obrigatório");
      if (!form.cpf.trim()) throw new Error("CPF é obrigatório");

      // Validar limite de pacientes do plano (apenas para novos pacientes)
      if (!patient?.id && !canAddPatient()) {
        setShowUpgradeModal(true);
        return;
      }

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error("Sessão expirada.");

      // Upload avatar se houver
      let avatarUrl = form.avatar_url || null;
      if (avatarFile) {
        const fileExt = avatarFile.name.split(".").pop();
        const fileName = `${user.id}-${Date.now()}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, avatarFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
        avatarUrl = data.publicUrl;
      }

      // 1) Tenta com conjunto A (M/F/O/N)
      const sexoA = normalizeSexToA(form.sexo || null);
      const payload = {
        owner_id: user.id,
        full_name: form.full_name.trim(),
        cpf: form.cpf.trim(),
        email: form.email?.trim() || null,
        celular: form.celular?.trim() || null,
        data_nascimento: form.data_nascimento || null,
        sexo: sexoA,
        avatar_url: avatarUrl
      };

      let error: { message?: string } | null = null;

      if (patient?.id) {
        const result = await supabase.from("patients_onboarding").update(payload).eq("id", patient.id);
        error = result.error;
      } else {
        const result = await supabase.from("patients_onboarding").insert(payload);
        error = result.error;
      }

      // 2) Fallback: se o banco usa pt (masculino/feminino/outro/nao_informado)
      if (isSexConstraintError(error)) {
        const sexoB = normalizeSexToB(form.sexo || null);
        const payloadB = { ...payload, sexo: sexoB };

        if (patient?.id) {
          const retry = await supabase.from("patients_onboarding").update(payloadB).eq("id", patient.id);
          if (retry.error) throw new Error(retry.error.message);
        } else {
          const retry = await supabase.from("patients_onboarding").insert(payloadB);
          if (retry.error) throw new Error(retry.error.message);
        }
      } else if (error) {
        throw new Error(error.message);
      }

      // Atualizar contagem de pacientes no store (apenas se criou novo paciente)
      if (!patient?.id) {
        await refreshPatientsCount();
      }

      toast.success(patient?.id ? "Paciente atualizado com sucesso!" : "Paciente criado com sucesso!");
      onSaved();
      onClose();
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "Falha ao salvar paciente";
      setErr(errorMessage);
      toast.error(errorMessage);
    } finally {
      setBusy(false);
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    // Se tentando abrir e é novo paciente, verificar limite
    if (isOpen && !patient && !canAddPatient()) {
      // Não abrir o sheet, apenas mostrar modal de upgrade
      setShowUpgradeModal(true);
      onClose();
      return;
    }
    // Permitir fechar normalmente
    if (!isOpen) {
      onClose();
    }
  };

  // Se tentando abrir para novo paciente e limite atingido, não renderizar o Sheet
  const shouldShowSheet = open && (!patient ? canAddPatient() : true);
  
  // Se tentando abrir mas não pode adicionar, mostrar modal de upgrade
  useEffect(() => {
    if (open && !patient && !canAddPatient()) {
      setShowUpgradeModal(true);
    }
  }, [open, patient, canAddPatient]);

  // Fechar modal de upgrade automaticamente quando puder adicionar paciente
  useEffect(() => {
    if (showUpgradeModal && canAddPatient()) {
      setShowUpgradeModal(false);
    }
  }, [showUpgradeModal, canAddPatient]);

  return (
    <>
      <Sheet open={shouldShowSheet} onOpenChange={handleOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {patient ? "Editar Paciente" : "Novo Paciente"}
          </SheetTitle>
          <SheetDescription>
            {patient
              ? "Atualize as informações do paciente abaixo."
              : "Preencha as informações para cadastrar um novo paciente."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Avatar upload */}
          <div className="flex justify-center">
            <div className="relative">
              {avatarPreview ? (
                <div className="relative">
                  <Avatar className="w-24 h-24 border-2 border-primary">
                    <AvatarImage src={avatarPreview} alt="Avatar" />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      <User className="h-8 w-8" />
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={removeAvatar}
                    className="absolute top-0 right-0 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center cursor-pointer hover:border-primary transition">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Nome completo */}
            <div className="space-y-2">
              <Label htmlFor="full_name">
                Nome completo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Nome completo do paciente"
              />
            </div>

            {/* CPF */}
            <div className="space-y-2">
              <Label htmlFor="cpf">
                CPF <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cpf"
                value={form.cpf}
                onChange={e => setForm(f => ({ ...f, cpf: e.target.value }))}
                placeholder="000.000.000-00"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="email@exemplo.com"
              />
            </div>

            {/* Celular */}
            <div className="space-y-2">
              <Label htmlFor="celular">Celular</Label>
              <Input
                id="celular"
                value={form.celular}
                onChange={e => setForm(f => ({ ...f, celular: e.target.value }))}
                placeholder="(00) 00000-0000"
              />
            </div>

            {/* Data de nascimento */}
            <div className="space-y-2">
              <Label htmlFor="data_nascimento">Data de nascimento</Label>
              <Input
                id="data_nascimento"
                type="date"
                value={form.data_nascimento}
                onChange={e => setForm(f => ({ ...f, data_nascimento: e.target.value }))}
              />
            </div>

            {/* Sexo */}
            <div className="space-y-2">
              <Label htmlFor="sexo">Sexo</Label>
              <Select
                value={form.sexo || ""}
                onValueChange={(value) => setForm(f => ({ ...f, sexo: value }))}
              >
                <SelectTrigger id="sexo">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="F">Feminino</SelectItem>
                  <SelectItem value="O">Outro</SelectItem>
                  <SelectItem value="N">Não informado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {err && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {err}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="flex-1"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {busy ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      </SheetContent>
      </Sheet>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason="patients"
        currentCount={patientsCount}
      />
    </>
  );
}

